import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput } from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from '../icons/AppIcon';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import {
  NODE_TYPE_META,
  NODE_CATEGORY_META,
  FlowNodeType,
  NodeTypeMeta,
  NodeCategory,
  getFrequentlyUsedNodeTypes,
} from './flow-types';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface NodeTypePickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (type: FlowNodeType) => void;
}

export const NodeTypePicker: React.FC<NodeTypePickerProps> = ({ visible, onClose, onSelect }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [frequent, setFrequent] = useState<NodeTypeMeta[]>([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<NodeCategory | null>(null);

  useEffect(() => {
    if (!visible) return;
    getFrequentlyUsedNodeTypes().then(setFrequent);
    setSearch('');
    setActiveCategory(null);
  }, [visible]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return NODE_TYPE_META.filter((item) => {
      if (item.hidden) return false; // e.g. Start — exists automatically, never user-addable
      if (activeCategory && item.category !== activeCategory) return false;
      if (!query) return true;
      return item.label.toLowerCase().includes(query) || item.description.toLowerCase().includes(query);
    });
  }, [search, activeCategory]);

  const grouped = useMemo(() => {
    const byCategory = new Map<NodeCategory, NodeTypeMeta[]>();
    for (const item of filtered) {
      const list = byCategory.get(item.category) || [];
      list.push(item);
      byCategory.set(item.category, list);
    }
    return NODE_CATEGORY_META.map((cat) => ({ cat, items: byCategory.get(cat.id) || [] })).filter((g) => g.items.length > 0);
  }, [filtered]);

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={[styles.modalHeader, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.modalTitle, { textAlign: rtl.textStart }]}>{t('flows.nodePicker.title')}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchSection}>
            <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="search" size={18} color={colors.textLight} style={rtl.marginEnd(spacing.xs)} />
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder={t('flows.nodePicker.searchPlaceholder')}
                placeholderTextColor={colors.textLight as string}
                style={[styles.searchInput, { textAlign: rtl.textStart }]}
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close-circle" size={18} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={NODE_CATEGORY_META}
              keyExtractor={(item) => item.id}
              contentContainerStyle={[styles.categoryRow, { flexDirection: rtl.rowDirection }]}
              renderItem={({ item }) => {
                const active = activeCategory === item.id;
                return (
                  <TouchableOpacity
                    style={[styles.categoryChip, { flexDirection: rtl.rowDirection }, active && styles.categoryChipActive]}
                    onPress={() => setActiveCategory(active ? null : item.id)}
                  >
                    <AppIcon name={item.icon} size={14} color={(active ? colors.white : colors.text.secondary) as string} />
                    <Text style={[styles.categoryChipText, active && styles.categoryChipTextActive]}>
                      {t(`flows.nodePicker.category.${item.id}` as any)}
                    </Text>
                  </TouchableOpacity>
                );
              }}
            />
          </View>

          {frequent.length > 0 && !search && !activeCategory && (
            <View style={styles.frequentSection}>
              <Text style={[styles.frequentLabel, { textAlign: rtl.textStart }]}>{t('flows.nodePicker.frequentlyUsed')}</Text>
              <View style={[styles.frequentRow, { flexDirection: rtl.rowDirection }]}>
                {frequent.map((item) => (
                  <TouchableOpacity key={item.type} style={[styles.frequentChip, { flexDirection: rtl.rowDirection }]} onPress={() => onSelect(item.type)}>
                    <AppIcon name={item.icon} size={16} color={item.color} />
                    <Text style={styles.frequentChipText} numberOfLines={1}>{item.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {grouped.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="search-outline" size={40} color={colors.textLight} />
              <Text style={styles.emptyStateText}>{t('flows.nodePicker.noResults')}</Text>
            </View>
          ) : (
            <FlatList
              data={grouped}
              keyExtractor={(g) => g.cat.id}
              renderItem={({ item: group }) => (
                <View>
                  <Text style={[styles.sectionLabel, { textAlign: rtl.textStart }]}>{t(`flows.nodePicker.category.${group.cat.id}` as any)}</Text>
                  {group.items.map((item) => (
                    <TouchableOpacity key={item.type} style={[styles.option, { flexDirection: rtl.rowDirection }]} onPress={() => onSelect(item.type)}>
                      <View style={[styles.iconWrap, { backgroundColor: `${item.color}15` }]}>
                        <AppIcon name={item.icon} size={20} color={item.color} />
                      </View>
                      <View style={styles.optionContent}>
                        <View style={[styles.optionTitleRow, { flexDirection: rtl.rowDirection }]}>
                          <Text style={[styles.optionLabel, { textAlign: rtl.textStart }]}>{item.label}</Text>
                          {item.whatsappOnly && (
                            <View style={styles.badge}>
                              <Text style={styles.badgeText}>{t('flows.nodePicker.whatsappOnly')}</Text>
                            </View>
                          )}
                          {item.facebookOnly && (
                            <View style={styles.badge}>
                              <Text style={styles.badgeText}>{t('flows.nodePicker.facebookOnly')}</Text>
                            </View>
                          )}
                        </View>
                        <Text style={[styles.optionDescription, { textAlign: rtl.textStart }]}>{item.description}</Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            />
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '85%',
    paddingBottom: spacing.lg,
  },
  modalHeader: { alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  modalTitle: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  searchSection: { paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.sm, gap: spacing.sm },
  searchContainer: {
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
  },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, paddingVertical: 0 },
  categoryRow: { gap: spacing.xs },
  categoryChip: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full ?? 999,
    backgroundColor: colors.lightGray,
  },
  categoryChipActive: { backgroundColor: colors.primary },
  categoryChipText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  categoryChipTextActive: { color: colors.white },
  frequentSection: { paddingHorizontal: spacing.lg, paddingTop: spacing.sm, paddingBottom: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  frequentLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.textLight, textTransform: 'uppercase', marginBottom: spacing.xs },
  frequentRow: { flexWrap: 'wrap', gap: spacing.xs },
  frequentChip: {
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.lightGray,
  },
  frequentChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textDark },
  sectionLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textLight,
    textTransform: 'uppercase',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
  },
  option: { alignItems: 'center', padding: spacing.md, paddingHorizontal: spacing.lg, gap: spacing.md },
  iconWrap: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  optionContent: { flex: 1 },
  optionTitleRow: { alignItems: 'center', gap: spacing.xs },
  optionLabel: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginBottom: spacing.xs / 2 },
  optionDescription: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  badge: { backgroundColor: '#FEF3C7', paddingHorizontal: spacing.xs, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  badgeText: { fontSize: 10, color: '#D97706', fontFamily: typography.fontFamily.medium },
  emptyState: { alignItems: 'center', paddingVertical: spacing.xxl, gap: spacing.sm },
  emptyStateText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.regular },
});
